import { ResolvedMediaItem } from "@rista/core/customerDisplay";

/**
 * MediaSlide — the contract both variants render against.
 *
 * The bodies genuinely diverge (DOM `<video>/<img>` vs react-native-video/`<Image>`), so each
 * variant keeps its own; only the props and the shared black letterbox background live here, so
 * there is one definition of each (rule 4: a split folder always has a shared.ts).
 */
export interface MediaSlideProps {
  item: ResolvedMediaItem;
  /**
   * What shows in the letterbox gutter a `contain` fit leaves around the media.
   *
   * Defaults to `MEDIA_BACKGROUND_COLOR` — black is right for the customer display, which is a
   * dedicated screen showing merchant artwork on its own.
   *
   * ⚠️ **Pass `"transparent"` where the gutter belongs to the HOST.** The kiosk's attract
   * carousel is the case: its `<Image>` slides paint no background at all, so the gutter shows
   * `theme.primary` from the idle screen's root through them. A video hardcoded to black broke
   * that symmetry — image banners sat in the merchant's brand colour and video banners in black
   * letterboxing. Transparent lets both inherit the same ancestor, which also keeps it correct
   * on the catalog rail, where the surface behind is a different one.
   */
  backgroundColor?: string;
  /**
   * Restart the clip when it reaches the end. Defaults to `true` — the customer display shows
   * one item on a loop and has nothing to advance to.
   *
   * ⚠️ Pass `false` where something downstream is WAITING for the end. A looping video never
   * fires `onEnd`, so a carousel that advances on it would sit on that slide forever.
   */
  loop?: boolean;
  /**
   * Hold playback. Defaults to `false`.
   *
   * ⚠️ A carousel keeps off-screen slides mounted, so without this every video in the list plays
   * at once — burning decode on clips nobody can see, and, worse, firing their `onEnd` while a
   * different slide is on screen.
   */
  paused?: boolean;
  /** The clip reached its end. Never fires while `loop` is true. VIDEO ONLY. */
  onEnd?: () => void;
  /**
   * The clip could not be played. VIDEO ONLY — the image path has its own URL-fallback ladder.
   *
   * ⚠️ Worth wiring wherever `onEnd` is: a caller waiting on the end of a video that never
   * loads waits forever, so a dead URL would stall a carousel rather than skipping past it.
   */
  onError?: () => void;
}

/** Letterbox behind a contain-fitted image or video. The default; override per host. */
export const MEDIA_BACKGROUND_COLOR = "#000000";
